import { useEffect, useState } from "react";
import { copy } from "shared/assets";
import { Tooltip } from "shared/components";
import { CheckBoxWrapper } from "shared/components/Form/Fields/Option/styles";
import { IPermission } from "shared/types/general";
import { getLastIdFromPath } from "shared/utils";

const {
  permissions: { editPermissions: permissionsCopy },
} = copy;

export const PermissionCheckbox = ({
  register,
  userId,
  name,
  defaultValue,
  dependencies,
  dependencyMap,
  allPermissions,
}: {
  register: any;
  value: string;
  userId: string;
  name: string;
  defaultValue: boolean;
  dependencies?: { id: string }[];
  dependencyMap?: Record<string, string[]>;
  allPermissions?: IPermission[];
}) => {
  const [referenceElement, setReferenceElement] = useState<Element | null>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    e.target.checked = checked;

    if (checked && dependencies) {
      dependencies.forEach(({ id }) => {
        const dependentCheckbox = document.getElementById(
          `${userId}#${getLastIdFromPath(id)}`
        ) as HTMLInputElement;

        if (dependentCheckbox) {
          dependentCheckbox.disabled = true;
          dependentCheckbox.checked = true;
          dependentCheckbox.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }
      });
    } else if (!checked && dependencies) {
      dependencies.forEach(({ id }) => {
        const dependentId = getLastIdFromPath(id);
        const dependentCheckbox = document.getElementById(
          `${userId}#${dependentId}`
        ) as HTMLInputElement;

        if (dependentCheckbox) {
          const hasOtherDependencies = Object.entries(dependencyMap || {}).some(
            ([permId, deps]) =>
              permId !== dependentId &&
              deps.includes(dependentId) &&
              (
                document.getElementById(
                  `${userId}#${permId}`
                ) as HTMLInputElement
              )?.checked
          );

          if (!hasOtherDependencies) {
            dependentCheckbox.disabled = false;
            dependentCheckbox.checked = false;
            dependentCheckbox.dispatchEvent(
              new Event("change", { bubbles: true })
            );
          }
        }
      });
    }
  };

  useEffect(() => {
    handleChange({
      target: { checked: defaultValue },
    } as React.ChangeEvent<HTMLInputElement>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  // TODO small bug where the incorrect tooltip is shown when the dependency is unchecked
  useEffect(() => {
    const checkDisabled = () => {
      const checkbox = document.getElementById(name) as HTMLInputElement;
      if (!checkbox) return;

      const parentPermissionId = Object.entries(dependencyMap || {}).filter(
        ([permId, deps]) => {
          const permCheckbox = document.getElementById(
            `${userId}#${permId}`
          ) as HTMLInputElement;

          return deps.includes(name.split("#")[1]) && permCheckbox?.checked;
        }
      )[0]?.[0]; // Get the first one that matches

      const parentPermission = allPermissions?.find(
        (p) => getLastIdFromPath(p.permission.id) === parentPermissionId
      );

      const tooltip = permissionsCopy.messages.disabled
        .replace("%s", parentPermission?.permission.label!)
        .toString();
      setShowTooltip(checkbox?.disabled ? tooltip : null);
    };

    // Initial check
    checkDisabled();

    // Create observer to watch for DOM changes
    const observer = new MutationObserver(checkDisabled);

    // Start observing the document
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ["disabled", "checked"],
    });

    return () => observer.disconnect();
  }, [allPermissions, dependencyMap, name, userId]);

  return (
    <CheckBoxWrapper>
      <div
        ref={setReferenceElement}
        style={{ width: "fit-content", margin: "0 auto" }}
      >
        <input
          id={name}
          name={name}
          type="checkbox"
          ref={register()}
          defaultChecked={defaultValue}
          onChange={handleChange}
        />
      </div>
      {showTooltip && (
        <Tooltip
          content={<span>{showTooltip}</span>}
          referenceElement={referenceElement}
          placement="top-end"
          maxWidth="none"
        />
      )}
    </CheckBoxWrapper>
  );
};
